import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaintenanceService, CreateMaintenancePayload, JoinedLog } from '../service/maintenance.service';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeService } from '../service/theme.service';
import { CognitoService } from '../service/cognito.service';
import { environment } from '../environments/environment';

type Tab = 'maintenance' | 'xbee';

@Component({
    selector: 'app-maintenance-logs',
    templateUrl: './maintenance-logs.component.html',
    styleUrls: ['./maintenance-logs.component.css', '../../global-elements.css'],
    standalone: false
})
export class MaintenanceLogsComponent implements OnInit, OnDestroy {
  activeTab: Tab = 'maintenance';
  loading = false;
  errorMsg = '';
  results$: Observable<JoinedLog[]> = of([]);


  // shared header form (timestamps are epoch seconds)
  head = this.fb.group({
    thing_name: ['', Validators.required],
    user_sub_dml: [''],
    user_sub_xcl: [''],
    timestamp: [null as number | null] // bound via appEpochDatetimeLocal
  });

  maintenanceKeys = ['pcb','antenna','xbee','sim','device_case','cover','bracket'] as const;

  xbeeMainKeys = [
    'network_technology','access_point','airplane_mode','carrier',
    'api_enable','baud_rate','packetization_timeout','flow_control','parity','stop_bits'
  ] as const;

  dioPins = Array.from({length: 15}, (_,i)=>`DIO${i}`) as Array<
    'DIO0'|'DIO1'|'DIO2'|'DIO3'|'DIO4'|'DIO5'|'DIO6'|'DIO7'|'DIO8'|'DIO9'|'DIO10'|'DIO11'|'DIO12'|'DIO13'|'DIO14'
  >;

  xbeeTailKeys = ['pr','pd','m0','m1'] as const;

  maintenance!: FormGroup;
  xbee!: FormGroup;

  userSub: string = '';
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private api: MaintenanceService,
    private route: ActivatedRoute,
    public theme: ThemeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private cognitoService: CognitoService

  ) {
    this.maintenance = this.fb.group({});
    for (const maintenanceKey of this.maintenanceKeys) {
      this.maintenance.addControl(maintenanceKey, this.fb.control(''));
      this.maintenance.addControl(`${maintenanceKey}_timestamp`, this.fb.control(null as number | null));
    }

    this.xbee = this.fb.group({});
    for (const xbeeMainKey of this.xbeeMainKeys) {
      this.xbee.addControl(xbeeMainKey, this.fb.control(''));
      const tsKey = xbeeMainKey === 'baud_rate' ? 'baud_timestamp' : `${xbeeMainKey}_timestamp`;
      this.xbee.addControl(tsKey, this.fb.control(null as number | null));
    }
    for (const pin of this.dioPins) {
      this.xbee.addControl(pin, this.fb.control(''));
      this.xbee.addControl(`${pin}_timestamp`, this.fb.control(null as number | null));
    }
    for (const xbeeTailKey of this.xbeeTailKeys) {
      this.xbee.addControl(xbeeTailKey, this.fb.control(''));
      this.xbee.addControl(`${xbeeTailKey}_timestamp`, this.fb.control(null as number | null));
    }

    this.setupTimestampAutoUpdate();
  }
  currentDateTime = () =>{
    const tsCtrl = this.head.get('timestamp');
    const nowSec = Math.floor(Date.now() / 1000);
    tsCtrl?.setValue(nowSec, { emitEvent: false });
  };

  private setupTimestampAutoUpdate() {
    const register = (group: FormGroup, fieldKey: string, tsKey: string) => {
      const control = group.get(fieldKey);
      const tsControl = group.get(tsKey);
      if (!control || !tsControl) { return; }

      const sub = control.valueChanges.subscribe(() => {
        const nowSec = Math.floor(Date.now() / 1000);
        tsControl.setValue(nowSec, { emitEvent: false });
      });

      this.subscriptions.push(sub);
    };

    for (const key of this.maintenanceKeys) {
      register(this.maintenance, key, `${key}_timestamp`);
    }

    for (const key of this.xbeeMainKeys) {
      const tsKey = key === 'baud_rate' ? 'baud_timestamp' : `${key}_timestamp`;
      register(this.xbee, key, tsKey);
    }

    for (const pin of this.dioPins) {
      register(this.xbee, pin, `${pin}_timestamp`);
    }

    for (const key of this.xbeeTailKeys) {
      register(this.xbee, key, `${key}_timestamp`);
    }
  }

  // Read :thing from route and prefill immediately
  async ngOnInit(): Promise<void> {
    //Set an interval to keep the timestamp field updated
    setInterval(()=>{
      this.currentDateTime();
    },60000);

    //Verifies that the current User is allowed to view this component
    this.cognitoService.getCurrentRole(
      [environment.users.role.administrator],
      [environment.users.superAdmin]
    ); //Role Check
    this.route.paramMap.subscribe(pm => {
      const thing = pm.get('thing');
      if (!thing) {
        this.errorMsg = 'Missing thing_name in route.';
        return;
      }
      this.head.patchValue({ thing_name: thing }, { emitEvent: false });
      this.prefillFromLatest();
    });
    void this.setCurrentUserSubs();

  }

  private async setCurrentUserSubs() {
    try {
      const user = await this.cognitoService.getUser();
      this.userSub = user?.attributes?.sub || '';
      this.head.patchValue(
        { user_sub_dml: this.userSub, user_sub_xcl: this.userSub },
        { emitEvent: false }
      );
      this.cdr.markForCheck(); // OnPush
    } catch {
      // ignore; leave fields blank if user not available
    }
  }

  setTab(tab: Tab) { this.activeTab = tab; }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  prefillFromLatest() {
    this.errorMsg = '';
    if (this.head.invalid) { this.errorMsg = 'thing_name is required'; return; }

    const thing = String(this.head.value.thing_name);
    this.loading = true;
    this.api.getLatestByThingName(thing).subscribe({
      next: rows => {
        this.loading = false;
        const r = rows[0];
        // Update URL to /maintenance-log/:thing without pushing history
        this.router.navigate(['../', thing], { relativeTo: this.route, replaceUrl: true });
        if (!r) {
          this.clearAllForThing(thing);
          this.errorMsg = 'No latest log found for this thing_name.';
          return;
        }
        this.patchAll(r);
      },
      error: e => { this.loading = false; this.errorMsg = e?.error?.message || e.message || 'Prefill failed'; }
    });
  }

  submitCreate() {
    this.errorMsg = '';
    if (this.head.invalid) { this.errorMsg = 'thing_name is required'; return; }

    // Ensure timestamp has a value (epoch seconds) on submit
    const tsCtrl = this.head.get('timestamp');
    const nowSec = Math.floor(Date.now() / 1000);

    tsCtrl?.setValue(nowSec, { emitEvent: false });


    const anchorTs = Number(this.head.value.timestamp);
    // short-hands to read values
    const m = this.maintenance;
    const x = this.xbee;
    const s = (g: FormGroup, key: string) => String(g.get(key)?.value ?? '');
    const t = (g: FormGroup, key: string) => Number((g.get(key)?.value ?? anchorTs));

    const payload: CreateMaintenancePayload = {
      // shared
      thing_name: String(this.head.value.thing_name),
      user_sub_dml: String(this.head.value.user_sub_dml || ''),
      user_sub_xcl: String(this.head.value.user_sub_xcl || ''),
      timestamp: anchorTs,

      // ---- device_maintenance_log (explicit) ----
      pcb: s(m, 'pcb'),                 pcb_timestamp: t(m, 'pcb_timestamp'),
      antenna: s(m, 'antenna'),         antenna_timestamp: t(m, 'antenna_timestamp'),
      xbee: s(m, 'xbee'),               xbee_timestamp: t(m, 'xbee_timestamp'),
      sim: s(m, 'sim'),                 sim_timestamp: t(m, 'sim_timestamp'),
      device_case: s(m, 'device_case'), device_case_timestamp: t(m, 'device_case_timestamp'),
      cover: s(m, 'cover'),             cover_timestamp: t(m, 'cover_timestamp'),
      bracket: s(m, 'bracket'),         bracket_timestamp: t(m, 'bracket_timestamp'),

      // ---- xbee_config_log core (explicit) ----
      network_technology: s(x, 'network_technology'),
      network_technology_timestamp: t(x, 'network_technology_timestamp'),

      access_point: s(x, 'access_point'),
      access_point_timestamp: t(x, 'access_point_timestamp'),

      airplane_mode: s(x, 'airplane_mode'),
      airplane_mode_timestamp: t(x, 'airplane_mode_timestamp'),

      carrier: s(x, 'carrier'),
      carrier_timestamp: t(x, 'carrier_timestamp'),

      api_enable: s(x, 'api_enable'),
      api_enable_timestamp: t(x, 'api_enable_timestamp'),

      baud_rate: s(x, 'baud_rate'),
      // schema uses `baud_timestamp` (not `baud_rate_timestamp`)
      baud_timestamp: t(x, 'baud_timestamp'),

      packetization_timeout: s(x, 'packetization_timeout'),
      packetization_timeout_timestamp: t(x, 'packetization_timeout_timestamp'),

      flow_control: s(x, 'flow_control'),
      flow_control_timestamp: t(x, 'flow_control_timestamp'),

      parity: s(x, 'parity'),
      parity_timestamp: t(x, 'parity_timestamp'),

      stop_bits: s(x, 'stop_bits'),
      stop_bits_timestamp: t(x, 'stop_bits_timestamp'),

      // ---- DIO0..DIO14 (explicit) ----
      DIO0: s(x, 'DIO0'),   DIO0_timestamp: t(x, 'DIO0_timestamp'),
      DIO1: s(x, 'DIO1'),   DIO1_timestamp: t(x, 'DIO1_timestamp'),
      DIO2: s(x, 'DIO2'),   DIO2_timestamp: t(x, 'DIO2_timestamp'),
      DIO3: s(x, 'DIO3'),   DIO3_timestamp: t(x, 'DIO3_timestamp'),
      DIO4: s(x, 'DIO4'),   DIO4_timestamp: t(x, 'DIO4_timestamp'),
      DIO5: s(x, 'DIO5'),   DIO5_timestamp: t(x, 'DIO5_timestamp'),
      DIO6: s(x, 'DIO6'),   DIO6_timestamp: t(x, 'DIO6_timestamp'),
      DIO7: s(x, 'DIO7'),   DIO7_timestamp: t(x, 'DIO7_timestamp'),
      DIO8: s(x, 'DIO8'),   DIO8_timestamp: t(x, 'DIO8_timestamp'),
      DIO9: s(x, 'DIO9'),   DIO9_timestamp: t(x, 'DIO9_timestamp'),
      DIO10: s(x, 'DIO10'), DIO10_timestamp: t(x, 'DIO10_timestamp'),
      DIO11: s(x, 'DIO11'), DIO11_timestamp: t(x, 'DIO11_timestamp'),
      DIO12: s(x, 'DIO12'), DIO12_timestamp: t(x, 'DIO12_timestamp'),
      DIO13: s(x, 'DIO13'), DIO13_timestamp: t(x, 'DIO13_timestamp'),
      DIO14: s(x, 'DIO14'), DIO14_timestamp: t(x, 'DIO14_timestamp'),

      // ---- tail (explicit) ----
      pr: s(x, 'pr'),   pr_timestamp: t(x, 'pr_timestamp'),
      pd: s(x, 'pd'),   pd_timestamp: t(x, 'pd_timestamp'),
      m0: s(x, 'm0'),   m0_timestamp: t(x, 'm0_timestamp'),
      m1: s(x, 'm1'),   m1_timestamp: t(x, 'm1_timestamp'),
    };

    this.loading = true;
    this.api.create(payload).subscribe({
      next: () => { this.loading = false; },
      error: e => { this.loading = false; this.errorMsg = e?.error?.message || e.message || 'Create failed'; }
    });
  }

  private patchAll(r: JoinedLog) {
    // head
    this.head.patchValue({
      thing_name: r.thing_name,
      user_sub_dml: r.dml_user_sub || '',
      user_sub_xcl: r.xcl_user_sub || '',
      timestamp: r.timestamp ?? null
    }, { emitEvent: false });
    //overwrite row timestamp
    this.currentDateTime();

    // maintenance
    for (const k of this.maintenanceKeys) {
      (this.maintenance.get(k))?.setValue((r as any)[k] ?? '', { emitEvent: false });
      (this.maintenance.get(`${k}_timestamp`))?.setValue((r as any)[`${k}_timestamp`] ?? null, { emitEvent: false });
    }

    // xbee main
    for (const k of this.xbeeMainKeys) {
      (this.xbee.get(k))?.setValue((r as any)[k] ?? '', { emitEvent: false });
      const tsKey = k === 'baud_rate' ? 'baud_timestamp' : `${k}_timestamp`;
      (this.xbee.get(tsKey))?.setValue((r as any)[tsKey] ?? null, { emitEvent: false });
    }

    // DIOs
    for (const pin of this.dioPins) {
      (this.xbee.get(pin))?.setValue((r as any)[pin] ?? '', { emitEvent: false });
      (this.xbee.get(`${pin}_timestamp`))?.setValue((r as any)[`${pin}_timestamp`] ?? null, { emitEvent: false });
    }

    // tail
    for (const k of this.xbeeTailKeys) {
      (this.xbee.get(k))?.setValue((r as any)[k] ?? '', { emitEvent: false });
      (this.xbee.get(`${k}_timestamp`))?.setValue((r as any)[`${k}_timestamp`] ?? null, { emitEvent: false });
    }
  }

  trackByKey = (_: number, k: string) => k;
/** Clear all inputs for a new thing_name and update the URL */
clearAllForThing(thing: string) {
  // Reset header (keep the thing_name the user typed)
  this.head.patchValue({
    thing_name: thing,
    user_sub_dml: '',
    user_sub_xcl: '',
    timestamp: null
  }, { emitEvent: false });

  // Clear maintenance group
  for (const k of this.maintenanceKeys) {
    this.maintenance.get(k)?.setValue('', { emitEvent: false });
    this.maintenance.get(`${k}_timestamp`)?.setValue(null, { emitEvent: false });
  }

  // Clear xbee main
  for (const k of this.xbeeMainKeys) {
    this.xbee.get(k)?.setValue('', { emitEvent: false });
    const tsKey = k === 'baud_rate' ? 'baud_timestamp' : `${k}_timestamp`;
    this.xbee.get(tsKey)?.setValue(null, { emitEvent: false });
  }

  // Clear DIOs
  for (const pin of this.dioPins) {
    this.xbee.get(pin)?.setValue('', { emitEvent: false });
    this.xbee.get(`${pin}_timestamp`)?.setValue(null, { emitEvent: false });
  }

  // Clear tail
  for (const k of this.xbeeTailKeys) {
    this.xbee.get(k)?.setValue('', { emitEvent: false });
    this.xbee.get(`${k}_timestamp`)?.setValue(null, { emitEvent: false });
  }

  this.errorMsg = ''; // optional: clear any prior message
  this.cdr.markForCheck(); // OnPush: ensure view updates
}


}
